<template>
  <div class="order-board">
    <div class="board-columns">
      <div
        v-for="col in visibleColumns"
        :key="col.status"
        class="board-column"
      >
        <!-- Column header -->
        <div class="col-header" :style="{ borderTopColor: col.color }">
          <span class="col-label">{{ col.label }}</span>
          <span class="col-count">{{ (ordersByStatus[col.status] || []).length }}</span>
        </div>

        <!-- Cards -->
        <div class="col-cards">
          <div
            v-for="order in ordersByStatus[col.status] || []"
            :key="order.id"
            class="order-card"
            @click="$router.push(`/auftraege/${order.id}`)"
          >
            <div class="card-top">
              <span class="card-number">{{ order.order_number }}</span>
              <order-flags :order="order" />
            </div>

            <div class="card-description" v-if="order.description">
              {{ truncate(order.description, 48) }}
            </div>

            <div class="card-bottom">
              <router-link
                v-if="order.customer_id"
                :to="`/kunden/${order.customer_id}`"
                class="card-customer"
                @click.stop
              >
                {{ order.customer_name }}
              </router-link>
              <span v-else class="card-customer muted">{{ order.customer_name || '–' }}</span>

              <router-link
                v-if="order.employee_id"
                :to="`/mitarbeiter/${order.employee_id}`"
                class="card-avatar-link"
                :title="order.employee_name || '?'"
                @click.stop
              >
                <user-avatar
                  :src="order.employee_icon_url || getEmployee(order.employee_id)?.icon_url"
                  :seed="order.employee_id"
                  :name="order.employee_name || '?'"
                  :size="26"
                />
              </router-link>
              <span v-else class="no-employee" title="Kein Bearbeiter">
                <user-avatar :seed="'?'" name="?" :size="26" />
              </span>
            </div>
          </div>

          <div v-if="!(ordersByStatus[col.status] || []).length" class="col-empty">
            Keine Aufträge
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserAvatar from '@/components/UserAvatar.vue'
import OrderFlags from '@/components/OrderFlags.vue'
import { BOARD_COLUMNS } from '@/composables/useOrderStatus'

export default {
  name: 'OrderBoardView',
  components: { UserAvatar, OrderFlags },

  props: {
    orders:       { type: Array,   default: () => [] },
    employeeList: { type: Array,   default: () => [] },
    showArchived: { type: Boolean, default: false },
  },

  computed: {
    visibleColumns() {
      return this.showArchived
        ? BOARD_COLUMNS
        : BOARD_COLUMNS.filter(c => c.status !== 'archived')
    },

    ordersByStatus() {
      const map = {}
      for (const order of this.orders) {
        if (!map[order.status]) map[order.status] = []
        map[order.status].push(order)
      }
      return map
    },
  },

  methods: {
    getEmployee(id) {
      return this.employeeList.find(e => String(e.id) === String(id)) || null
    },

    truncate(str, n) {
      return str && str.length > n ? str.slice(0, n) + '…' : str
    },
  },
}
</script>

<style scoped>
.order-board {
  overflow-x: auto;
  padding-bottom: 1rem;
}

.board-columns {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  min-width: max-content;
  padding: 2px 2px 8px;
}

/* Column */
.board-column {
  width: 240px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 10px;
  overflow: hidden;
}

.col-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px 8px;
  background: #fff;
  border-top: 3px solid transparent;
  border-bottom: 1px solid #d0d7de;
}

.col-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: #24292f;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.col-count {
  font-size: 0.72rem;
  font-weight: 600;
  background: #eaeef2;
  color: #57606a;
  border-radius: 10px;
  padding: 1px 7px;
  min-width: 20px;
  text-align: center;
}

/* Cards container */
.col-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px 8px;
  min-height: 80px;
}

/* Empty state */
.col-empty {
  font-size: 0.78rem;
  color: #8c959f;
  text-align: center;
  padding: 16px 0;
}

/* Card */
.order-card {
  background: #ffffff;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 10px 11px 9px;
  cursor: pointer;
  transition: box-shadow 0.13s, border-color 0.13s, transform 0.1s;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.order-card:hover {
  border-color: #0969da;
  box-shadow: 0 2px 8px rgba(9, 105, 218, 0.13);
  transform: translateY(-1px);
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.card-number {
  font-size: 0.8rem;
  font-weight: 700;
  color: #0969da;
  font-family: 'SF Mono', 'Fira Code', monospace;
}

.card-urgent {
  color: #cf222e;
  font-size: 0.75rem;
}

.card-description {
  font-size: 0.76rem;
  color: #57606a;
  line-height: 1.35;
}

.card-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
}

.card-customer {
  font-size: 0.75rem;
  font-weight: 500;
  color: #24292f;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
  flex: 1;
}

.card-customer:hover {
  color: #0969da;
  text-decoration: underline;
}

.card-customer.muted {
  color: #8c959f;
}

.card-avatar-link {
  flex-shrink: 0;
  border-radius: 50%;
  display: block;
  transition: opacity 0.12s;
}

.card-avatar-link:hover {
  opacity: 0.8;
}

.no-employee {
  flex-shrink: 0;
  opacity: 0.35;
}
</style>
